import { Beer } from "lucide-react"
import { TIP_PRICE, TIP_URL } from "@/lib/connector"
import { cn } from "@/lib/utils"

/**
 * The tip button's label and its reason, the same wherever it appears. A beer,
 * with its price on the button: "Support the creator" asked for support and
 * then charged a fixed £6, which read as neither a donation nor a purchase.
 */
export const TIP_LABEL = `Buy me a beer · ${TIP_PRICE}`
export const TIP_REASON =
  "Daash is free and one person runs it. If it’s helped your training, the price of a beer helps keep the servers running."

/** "Buy me a beer", or nothing at all while there is no tip page to send people to. */
export function TipLink({ className, children }: { className?: string; children?: React.ReactNode }) {
  if (!TIP_URL) return null
  return (
    <a href={TIP_URL} target="_blank" rel="noopener" className={className}>
      {children ?? TIP_LABEL}
    </a>
  )
}

/**
 * The quiet ask: an outlined card that sits after the content and never
 * competes with "Connect". Its button stays neutral in every state. The
 * shared outline button hovers in the brand accent, which is red here, and a
 * phone keeps the hover after a tap, so it rendered as a second red button.
 */
export function TipCard({ className }: { className?: string }) {
  if (!TIP_URL) return null
  return (
    <div className={cn("rounded-2xl border border-border bg-card p-5 text-left shadow-sm sm:p-6", className)}>
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-daash-red/10 text-daash-red"
        >
          <Beer className="size-5" />
        </span>
        {/* Stacked: the reason for asking needs the card's full width. */}
        <div className="flex-1">
          <p className="font-semibold text-foreground">Enjoying Daash?</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{TIP_REASON}</p>
          <a
            href={TIP_URL}
            target="_blank"
            rel="noopener"
            className="mt-4 inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-border bg-background px-5 text-sm font-medium text-foreground transition-colors hover:border-foreground/25 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-daash-red/40"
          >
            <Beer className="size-4 text-daash-red" aria-hidden />
            {TIP_LABEL}
          </a>
        </div>
      </div>
    </div>
  )
}
